import { AudienceFootprintSupport, EnterpriseSupport, Invoice, Organization, OrganizationUser, UserAccess, UserRole, UserType } from 'api/interfaces'
import { defaultTransformFn, platformApi } from 'api/rtkQueryApi/platform/platformApi'

import { BillingCustomer } from '../../interfaces/BillingCustomer'
import { AgencyReserveType } from '../../interfaces/ReserveSettings'

const organizationsTags = platformApi.enhanceEndpoints({ addTagTypes: ['Organization', 'CanAccessAgencyPackages', 'BillingCustomers', 'OrganizationInvoices', 'OrganizationUsers'] })

const organizationsApi = organizationsTags.injectEndpoints({
  endpoints: build => ({
    getOrganization: build.query<Organization, void>({
      query: () => {
        return {
          url: `organizations`,
          method: 'GET',
          isContext: true
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: () => [{ type: 'Organization' }]
    }),
    canAccessAgencyPackages: build.query<boolean, void>({
      query: () => {
        return {
          url: `organizations/canAccessAgencyPackages`,
          method: 'GET',
          isContext: true
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: () => [{ type: 'CanAccessAgencyPackages' }]
    }),
    getOrganizationBillingCustomers: build.query<BillingCustomer[], void>({
      query: () => {
        return {
          url: `organizations/billingCustomers`,
          method: 'GET',
          isContext: true
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: () => [{ type: 'BillingCustomers' }]
    }),
    getAllOrganizationInvoices: build.query<Invoice[], void>({
      query: () => {
        return {
          url: `organizations/invoices`,
          method: 'GET',
          isContext: true
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: () => [{ type: 'OrganizationInvoices' }]
    }),
    getOrganizationUsers: build.query<OrganizationUser[], void>({
      query: () => {
        return {
          url: `organizations/users`,
          method: 'GET',
          isContext: true
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: () => [{ type: 'OrganizationUsers' }]
    }),
    getCurrentUserAccess: build.query<UserAccess, void>({
      query: () => {
        return {
          url: `organizations/users/current`,
          method: 'GET',
          isContext: true
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: () => [{ type: 'OrganizationUsers' }]
    }),
    getAudienceFootprintSupport: build.query<AudienceFootprintSupport, void>({
      query: () => {
        return {
          url: `organizations/audienceFootprintSupport`,
          method: 'GET',
          isContext: true
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: () => [{ type: 'Organization' }]
    }),
    getEnterpriseSupport: build.query<EnterpriseSupport, void>({
      query: () => {
        return {
          url: `organizations/enterpriseSupport`,
          method: 'GET',
          isContext: true
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: () => [{ type: 'Organization' }]
    }),
    updateOrganizationUserRoles: build.mutation<OrganizationUser[], { userId: string; userType: UserType; userRoles: UserRole[] }>({
      query: ({ userId, userType, userRoles }) => {
        return {
          url: `organizations/users/${userId}/roles`,
          method: 'PUT',
          isContext: true,
          data: { userType, userRoles }
        }
      },
      transformResponse: defaultTransformFn,
      invalidatesTags: () => [{ type: 'OrganizationUsers' }]
    }),
    setOrganizationAgencyReserveSettings: build.mutation<Organization, { reserveRate: number; agencyReserveType: AgencyReserveType }>({
      query: ({ reserveRate, agencyReserveType }) => {
        return {
          url: `organizations/agencyReserveSettings`,
          method: 'POST',
          isContext: true,
          data: { reserveRate: reserveRate, agencyReserveType: agencyReserveType }
        }
      },
      transformResponse: defaultTransformFn,
      invalidatesTags: () => [{ type: 'Organization' }]
    })
  }),
  overrideExisting: false
})

export const { useCanAccessAgencyPackagesQuery, useGetOrganizationQuery, useGetOrganizationBillingCustomersQuery, useGetAllOrganizationInvoicesQuery, useSetOrganizationAgencyReserveSettingsMutation, useGetOrganizationUsersQuery, useUpdateOrganizationUserRolesMutation, useGetAudienceFootprintSupportQuery, useGetEnterpriseSupportQuery, useGetCurrentUserAccessQuery } = organizationsApi
